import { BaseModel } from '@/models/BaseModel';
import { ContentRowData } from '@/typings/interfaces/data/grid/row';
import ColumnModel, { ContentAddonType } from '@/components/layout/column/ColumnModel';
import { RowSettingsModel } from '@/components/layout/row/RowSettingsModel';
import { ContentGrid, ContentType } from '@/components/layout/section/SectionBaseModel';
import { ContentColumnData } from '@/typings/interfaces/data/grid/column';
import { CustomCSSModel } from '@/models/CustomCSSModel';
import { getDeviceData } from '@/hooks/useDevice';
import { CustomCSS } from '@/typings/interfaces/data/settings/settings';
import store from '@/store';
import { SectionType } from '@/typings/enums/enums';
import { createEditFormUrl, generateUniqueId } from '@/utilities/Utilities';
import { SectionModelType } from '@/typings/types/types';
import { RepeatableModel } from '@/models/settings/RepeatableModel';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';

interface RowEditState {
  isCollapsed: boolean;
  isActive: boolean;
  isFocus: boolean;
}

interface RowConfigState {
  hasRegistrationAddon: boolean;
}

export enum RowRepeatableSortDirection {
  DESC = 'desc',
  ASC = 'asc'
}

export interface RowRepeatableState {
  enabled?: boolean;
  type?: string;
  columnGrid?: number;
  feedUrl?: string;
  limit?: number;

  sort?: {
    by?: string;
    direction?: RowRepeatableSortDirection;
  };
}

export interface RowModelState {
  modelId: string;
  id: string;
  classIdentifier: string;
  label: string;
  edit?: RowEditState;
  columns: ColumnModel[];
  settings: RowSettingsModel;
  config?: RowConfigState;
  repeatable?: RepeatableModel;
  customCss: CustomCSSModel | undefined;
}

export default class RowModel extends BaseModel<ContentRowData, RowModelState> {
  public section: SectionModelType;
  public campaignId: number;
  private deleted = false;

  private static reservedIds: string[] = [];

  constructor(data: ContentRowData, section: SectionModelType) {
    super(data);

    // Conflict found or no row id exists. Some other rows already uses this ID.
    // We need to refresh it to avoid conflicts with style or other functionality.
    if (!data.id || RowModel.reservedIds.includes(data.id)) {
      data.id = generateUniqueId();
    }

    RowModel.reservedIds.push(data.id);

    this.section = section;
    this.campaignId = section.campaignModel.id;

    this.parse(data);
  }

  getValueRoot() {
    return 'settings';
  }

  getTotalColumns() {
    return this.getData().columns.length;
  }

  parse(data: ContentRowData) {
    const state = this.state;
    state.modelId = this.modelId;
    state.id = data.id || generateUniqueId();
    state.classIdentifier = `grid__row--${state.id}`;
    state.label = data.settings?.basic?.name || 'Row';

    const editState = store.getters['campaign/state'].edit;

    if (!state.edit && editState?.enabled) {
      state.edit = {
        isFocus: false,
        isActive: false,
        isCollapsed: true
      };
    }

    state.config = {
      hasRegistrationAddon: RowModel.checkForAddons(data, 'registration')
    };

    if (data.settings?.advanced?.repeater) {
      if (state.repeatable) {
        state.repeatable.setData(data.settings.advanced.repeater);
      } else {
        state.repeatable = new RepeatableModel(data.settings.advanced.repeater);
      }
    } else {
      state.repeatable = undefined;
    }

    const columnMap: { [key: string]: ColumnModel } = {};

    if (state.columns) {
      state.columns.forEach((column) => {
        columnMap[column.state.id] = column;
      });
    }

    state.columns = data.columns.map((column) => {
      if (column.id && typeof columnMap[column.id] !== 'undefined') {
        const columnModel = columnMap[column.id];
        columnModel.setData(column);
        return columnModel;
      }

      return new ColumnModel(column, this);
    });

    if (state.settings) {
      state.settings.setData(data.settings ?? {});
    } else {
      state.settings = new RowSettingsModel(data.settings ?? {}, this.getSection());
    }

    if (data.settings?.advanced?.customcss) {
      state.customCss = RowModel.parseCustomCSSData(data, state.classIdentifier);
    }
  }

  public getSection() {
    return this.section;
  }

  public getEditUrl() {
    const editFormUrl = `/edit/campaign/row/${this.campaignId}/${this.section.state.id}?row_index=${this.index}`;
    return createEditFormUrl(editFormUrl);
  }

  reload() {
    this.section.reload();
  }

  setEditActiveState(activeState: boolean) {
    if (this.state.edit) {
      this.state.edit.isActive = activeState;
    }
  }

  canOpenGameSettings() {
    return this.getSection().getSectionType() === SectionType.FLOWPAGE;
  }

  canOpenSectionSettings() {
    return this.getSection().getSectionType() === SectionType.FLOWPAGE;
  }

  canInsertExtraColumn(): boolean {
    const rowState = this.state;
    if (rowState && rowState.settings) {
      const rowSettingsState = rowState.settings.state;

      if (
        rowSettingsState &&
        rowSettingsState.advanced &&
        rowSettingsState.advanced.repeater &&
        rowSettingsState.advanced.repeater.enable
      ) {
        return false;
      }
    }

    return rowState.columns.length < 4;
  }

  /**
   * TODO: Migrate this out in a interface or refactor the method parameters
   */
  setNewColumnActive(column: { model: ColumnModel; id: string }) {
    store.dispatch('editing/setActiveModel', {
      model: column.model
    });
  }

  async handleInsertExtraColumn() {
    const rowData = this.getData();
    const newColumn = {
      label: 'Column',
      size: 1,
      addons: [],
      id: generateUniqueId()
    };
    rowData.columns.push(newColumn);
    this.parse(rowData);
    this.redoParse();
    this.resizeColumns();
    await this.saveBlock();
    this.state.columns.forEach((column) => {
      if (column.state.id === newColumn.id && column.state.edit) {
        column.state.edit.isActive = true;
        this.setNewColumnActive({
          model: column,
          id: newColumn.id
        });
      }
    });
  }

  // get visible columns
  getVisibleColumns(): ColumnModel[] {
    return this.state.columns.filter((column) => {
      // find if visible
      const conditions = column.state.settings.state.advanced.visibilityCondition;

      if (conditions) {
        return conditions.check();
      }

      return true;
    });
  }

  // resize visible columns by length
  resizeColumns(onState?: boolean) {
    if (onState) {
      let size = 12;
      const columns = this.getVisibleColumns();
      switch (columns.length) {
        case 2:
          size = 6;
          break;

        case 3:
          size = 4;
          break;

        case 4:
          size = 3;
          break;

        case 6:
          size = 2;
          break;
      }
      columns.forEach((column) => {
        column.state.size = size;
      });
    } else {
      const data = this.getData();
      let size = 12;
      switch (data.columns.length) {
        case 2:
          size = 6;
          break;

        case 3:
          size = 4;
          break;

        case 4:
          size = 3;
          break;

        case 6:
          size = 2;
          break;
      }
      data.columns = data.columns.map((column) => {
        column.size = size;
        return column;
      });
    }
  }

  reorderColumns() {
    const data = this.getData();
    data.columns = this.state.columns.map((column) => {
      return column.getData();
    });

    this.parse(data);
  }

  async saveBlock() {
    return await this.section.saveBlock();
  }

  canDelete(): boolean {
    const sectionState = this.section.state;
    return !(sectionState.config && sectionState.config.clone);
  }

  canCopy(): boolean {
    const state = this.state;
    if (state && state.settings) {
      const settingsState = state.settings.state;
      return !(settingsState.advanced && settingsState.advanced.repeater && settingsState.advanced.repeater.enable);
    }
    return true;
  }

  canDuplicate(): boolean {
    const state = this.state;
    const hasRegistrationAddon = this.getAddons<AddonRegistrationModel>('registration').length > 0;
    if (hasRegistrationAddon) {
      return false;
    }

    if (state && state.settings) {
      const settingsState = state.settings.state;
      if (settingsState.advanced?.repeater && settingsState.advanced?.repeater?.enable) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  canPaste(): boolean {
    const clipboardState = store.getters['clipboard/state'];
    if (clipboardState.elementInClipboard instanceof RowModel) {
      const parentSection = this.getSection();
      if (!parentSection.state.config.hasRegistrationAddon) {
        return true;
      } else {
        const model = clipboardState.elementInClipboard;
        if (model && model.state && model.state.config) {
          return !model.state.config.hasRegistrationAddon;
        }
      }
    }
    return clipboardState.elementInClipboard instanceof RowModel;
  }

  canMoveUp(): boolean {
    return this.index > 0;
  }

  onMoveUp() {
    this.section.onMoveRowUp(this.index);
  }

  canMoveDown(): boolean {
    const section = this.section;
    const sectionState = section.state;
    const content = sectionState.content[0] as ContentGrid;
    return this.index < content.rows.length - 1;
  }

  onMoveDown() {
    this.section.onMoveRowDown(this.index);
  }

  onPasteElement() {
    const rowData = store.getters['clipboard/data'];

    if (rowData) {
      this.section.onPasteRow(rowData, this.index);
    }
  }

  async onPasteColumn(columnData: string, colIndex: number) {
    const data = this.getData();
    const columns = data.columns;
    const clone = JSON.parse(columnData) as ContentColumnData;
    clone.id = generateUniqueId();

    //If rows contains registration, remove all fields, as we can only have 1 registration
    if (clone && clone.addons && clone.addons.length > 0) {
      clone.addons.forEach((addon) => {
        if (addon.alias === 'registration' && addon.settings) {
          addon.settings.fields = [];
        }
      });

      //Filter out gameflow or gameplay, as we can only have one
      clone.addons = clone.addons.filter((addon) => {
        return addon.alias !== 'gameflow' && addon.alias !== 'gameplay';
      });
    }

    if (clone && clone.addons.length > 0) {
      clone.addons.forEach((addon) => {
        addon.id = generateUniqueId();
      });
    }

    //Insert new addon on correct index
    columns.splice(colIndex + 1, 0, clone);

    this.parse(data);
    this.resizeColumns();
    await this.saveBlock();

    this.state.columns.forEach((column, index) => {
      if (index === colIndex + 1) {
        store.dispatch('editing/setActiveModel', {
          model: column
        });
      }
    });
  }

  onMoveColumnUp(currentColumnIndex: number) {
    const data = this.getData();
    const columns = data.columns;
    const currentItem = columns[Number(currentColumnIndex)];
    columns[Number(currentColumnIndex)] = columns[currentColumnIndex - 1];
    columns[currentColumnIndex - 1] = currentItem;

    data.columns = columns;
    this.parse(data);

    store.dispatch('editing/setActiveModel', {
      model: this.state.columns[currentColumnIndex - 1]
    });
  }

  onMoveColumnDown(currentColumnIndex: number) {
    const data = this.getData();
    const columns = data.columns;
    const currentItem = columns[Number(currentColumnIndex)];

    columns[Number(currentColumnIndex)] = columns[currentColumnIndex + 1];
    columns[currentColumnIndex + 1] = currentItem;

    data.columns = columns;
    this.parse(data);

    store.dispatch('editing/setActiveModel', {
      model: this.state.columns[currentColumnIndex + 1]
    });
  }

  async onDelete() {
    await Promise.all(this.state.columns.map((column) => column.onDelete()));
  }

  //Delete row
  async delete() {
    if (this.deleted) {
      return;
    }

    this.deleted = true;

    await this.section.deleteRow(this.index);
  }

  //Delete a column from array
  async deleteColumn(columnIndex: number) {
    await this.state.columns[Number(columnIndex)].onDelete();
    const data = this.getData();
    data.columns.splice(columnIndex, 1);
    this.resizeColumns();
    this.parse(data);
    await this.saveBlock();
  }

  getColumnByIndex(index: number) {
    return this.state.columns[Number(index)];
  }

  private static checkForAddons(data: ContentRowData, alias: string): boolean {
    let hasAddon = false;
    if (data.columns) {
      data.columns.forEach((col) => {
        if (col.addons) {
          return col.addons.some((addon) => {
            if (addon.alias === alias) {
              hasAddon = true;
            }
          });
        }
      });
    }
    return hasAddon;
  }

  private static parseCustomCSSData(data: ContentRowData, identifier: string): CustomCSSModel | undefined {
    if (data.settings?.advanced?.customcss) {
      const useData = getDeviceData(data.settings.advanced.customcss) as CustomCSS | undefined;

      if (!useData) {
        return undefined;
      }
      if (useData.code && useData.code.length > 0) {
        return RowModel.constructCustomCSSState(useData, identifier);
      }
      return undefined;
    }
  }

  private static constructCustomCSSState(data: CustomCSS, identifier: string): CustomCSSModel {
    return new CustomCSSModel(data, identifier);
  }

  public shallSkipInitialParse(): boolean {
    return true;
  }

  public setEditingActive(): void {
    const state = this.state;
    if (state.edit) {
      state.edit.isActive = true;
      state.edit.isCollapsed = false;
    }

    if (this.section.state.edit) {
      this.section.state.edit.isCollapsed = false;
    }
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }

  public getAddons<Model extends ContentAddonType>(alias: string): Model[] {
    const addonModels: Model[] = [];

    this.state.columns.forEach((column) => {
      column.state.addons.forEach((addon) => {
        if (addon.alias === alias) {
          addonModels.push(addon as Model);
        }
      });
    });

    return addonModels;
  }

  public get index() {
    if (
      this.section.state.content &&
      this.section.state.content[0] &&
      this.section.state.content[0].type === ContentType.GRID
    ) {
      const index = this.section.state.content[0].rows.indexOf(this);

      if (index !== undefined) {
        return index;
      }
    }

    throw new Error(`Could not find row index in section`);
  }
}
